import { Component, inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CardComponent } from '../../shared/components/card/card.component';
import { UserService } from '../../services/user.service';
import { AuthService } from '../../services/auth.service';
import { MenuConfigResponse, Program, UserDataResponse, UserDetails } from '../../interfaces/user.interface';
import { Router } from '@angular/router';
import { MenuService } from '../../services/menu.service';
import { environment } from '../../../environments/environment';
import { ReferralDataService } from '../../services/referral-data.service';
import { ENROLLMENT_TYPE_BACKEND_MAPPINGS, STATIC_DATA } from '../../shared/constants/static-data.constants';
import { LeadDetailsService } from '../../services/lead-details.service';
import { SectionsService } from '../../services/sections.service';
import { LookupService } from '../../services/lookup.service';
import { ProgramService } from '../../services/program.service';
import { StorageService } from '../../services/storage.service';
import { SurveyFormComponent } from '../../shared/components/survey/survey-form/survey-form.component';
import { DialogConfig, DialogService } from '../../shared/services/dialog.service';
import { SharedService } from '../../shared/services/shared.service';
import { DropdownSelectComponent } from '../../shared/components/dropdown-select/dropdown-select.component';
import { PopupDialogComponent } from '../../shared/components/popup-dialog/popup-dialog.component';

@Component({
  selector: 'ppl-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule, CardComponent, SurveyFormComponent, DropdownSelectComponent, PopupDialogComponent],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.css'
})
export class DashboardComponent implements OnInit {
  userPrograms!: Program[];
  userData!: UserDetails;
  savedData: any;
  surveyMode = false;
  openSurvey: any;
  surveyData: any;
  notifications: string[] = [];
  pendingRoleProgram?: Program;
  selectedRoleForDialog?: number;
  showRoleDialog = false;
  @ViewChild('SurveyDialog') SurveyDialog!: TemplateRef<any>;
  private dialogSvc = inject(DialogService);

  constructor(private userService: UserService, private authService: AuthService, private router: Router,
    private menuService: MenuService,
    private referralDataService: ReferralDataService,
    private leadDetailsService: LeadDetailsService,
    private sectionService: SectionsService,
    private lookupService: LookupService,
    private programService: ProgramService,
    private storageService: StorageService,
    private sharedService: SharedService,
  ) { }

  ngOnInit(): void {

    this.authService.getUsers().subscribe(
      (data: UserDataResponse) => {
        this.userService.setUserDetails(data.responsedata);
        this.userData = data.responsedata;
        this.userPrograms = data.responsedata.programs;
        this.userService.setUserPrograms(this.userPrograms);
        this.userService.setSelectedLead(this.userPrograms[0].referenceId);
        this.getSurveyData();
        this.getBannerData();
      },
      (error) => {
        console.error(error);
      }
    );

    ///this.userService.programSubject.subscribe((data) => this.userPrograms = data);
  }

  getSurveyData() {
    if (!environment.isExternal) return;
    if (this.userData.usertype_lkp?.toLowerCase() !== 'participant' && this.userData.usertype_lkp?.toLowerCase() !== 'provider') return;
      const payload = {
        recordId: this.userPrograms[0].referenceId,
        userType: this.userData.usertype_lkp?.toLowerCase() === 'participant' ? 'EMP'
          : this.userData.usertype_lkp?.toLowerCase() === 'provider' ? 'IR'
          : null
      };

      this.sharedService.getSurvey(payload).subscribe((data) => {
        this.surveyData = (data as Record<string, unknown>)['responsedata'];
        if (this.surveyData?.srvyQuestions) {
          this.openSurveyDialog();
        }
      });
  }

  getBannerData() {
    if (
      this.userData.usertype_lkp?.toLowerCase() === 'participant' ||
      this.userData.usertype_lkp?.toLowerCase() === 'provider'
    ) {
      const payload = {
        programCode: this.userPrograms[0].program_code,
        recordId: this.userPrograms[0].referenceId,
        userType: this.userData.usertype_lkp,
      };
      this.userService.getBannerMessages(payload).subscribe((data) => {
        this.notifications = data?.responsedata ?? [];
      });
    } else {
      this.getNotifications();
    }
  }

  onProgramSelection = (program: Program) => {
    if (!environment.isExternal && program.roles && program.roles.length > 1) {
      this.pendingRoleProgram = program;
      this.selectedRoleForDialog = program.roles[0]?.role_sid;
      this.showRoleDialog = true;
      return;
    }

    this.proceedWithProgramSelection(program, program.roles?.[0]?.role_sid);
  }

  confirmRoleSelection() {
    if (!this.pendingRoleProgram) return;
    const program = this.pendingRoleProgram;
    const roleId = this.selectedRoleForDialog;
    this.pendingRoleProgram = undefined;
    this.showRoleDialog = false;
    this.proceedWithProgramSelection(program, roleId);
  }

  cancelRoleSelection() {
    this.pendingRoleProgram = undefined;
    this.selectedRoleForDialog = undefined;
    this.showRoleDialog = false;
  }

  private proceedWithProgramSelection(program: Program, roleId?: number) {
    this.userService.setSelectedRoleId(roleId);
    this.sectionService.clearTab();
    this.programService.fetchMenuAuthorization(program.program_code, 'MENU', roleId).subscribe(
      (data: MenuConfigResponse) => {
        const restrictedMenus = data.responsedata.length > 0 ? data.responsedata[0].restrictedMenus : [];
        this.userService.setRestrictedMenus(restrictedMenus ?? []);
        this.userService.setSelectedProgram(program);

        const menuItemstoDisplay = this.menuService.getAvailableMenus(program.program_code, restrictedMenus ?? []);

        if (!environment.isExternal) {
          const landingComponent = menuItemstoDisplay["Basic"][0].landing_component;
          if (landingComponent != 'globalConsumerSearch') {
            this.userService.setFirstSegment(landingComponent ?? 'participantEnrollmentList');
          }
          this.router.navigate([`/home/${landingComponent}`]);
          return;
        }
        //default route to participant enrollment list
        this.handleExternalLandingPage(program, `/home/participantEnrollmentList`);
      }, (error) => {
        console.error(error);
      }
    );
  }

  openSurveyDialog() {
    const dialogConfig: DialogConfig = {
      title: 'Survey',
      isOkText: 'OK',
      size: 'small',
      position: 'top',
      showClose: true,
      showOkButton: false,
      showButtons: false,
    };

    this.openSurvey = this.dialogSvc.openDialog(
      this.SurveyDialog,
      dialogConfig,
      () => this.handleCancel(),
      () => this.handleCancel()
    );
  }

  handlePopUpClose() {
    this.dialogSvc.closeDialog(this.openSurvey);
  }

  handleCancel(): void { return; }

  handleExternalLandingPage(program: Program, defaultRoute: string) {
    if (this.userData) {

      // get lookup post program selection
      this.lookupService.fetchAndStoreLookupValues();

      switch (this.userData.usertype_lkp?.toLowerCase()) {
        case 'participant':
          this.userService.setFirstSegment(STATIC_DATA.PARTICIPANT_ENROLLMENT_LIST);
          this.getEnrollmentAndNavigate(STATIC_DATA.PARTICIPANT_ENROLLMENT, program.program_code, program.referenceId);
          break;
        case 'provider':
          this.userService.setFirstSegment(STATIC_DATA.PROVIDER_ENROLLMENT_LIST);
          this.getEnrollmentAndNavigate(STATIC_DATA.PROVIDER_ENROLLMENT, program.program_code, program.referenceId);
          break;
        default:
          this.userService.setFirstSegment(STATIC_DATA.PARTICIPANT_ENROLLMENT_LIST);
          this.router.navigate([defaultRoute]);
      }
    }
  }

  getNotifications() {
      this.userService.fetchNotificationMessages('NY', this.userData.usertype_lkp).subscribe((data: any) => {
        this.notifications = data?.responsedata ?? [];
      });
  }

  // not used
  navigateToParticipant(referenceId: string, program_code: string) {
    this.userService.setLeadEntry('participantEnrollment');
    this.userService.setSelectedLead(referenceId);
    this.getEnrollmentAndNavigate(STATIC_DATA.PROVIDER_ENROLLMENT, program_code, referenceId)

  }

  // not used
  navigateToProvider(referenceId: string, program_code: string) {

    this.userService.setSelectedLead(referenceId);
    this.sectionService.setFlow('providerEnrollment');
    this.getEnrollmentAndNavigate(STATIC_DATA.PROVIDER_ENROLLMENT, program_code, referenceId);
  }

  getEnrollmentAndNavigate(entry: string, program_code: string, referenceId: string) {
    this.referralDataService.clearReferrralData();
    this.userService.setLeadEntry(entry);
    this.sectionService.setFlow(entry);
    this.userService.setSelectedLead(referenceId);

    this.leadDetailsService
      .fetchLeadDetailsApi(
        entry,
        program_code,
        Number(referenceId)
      )
      .subscribe((data) => {

        if (entry === STATIC_DATA.PROVIDER_ENROLLMENT) {
          this.savedData = data?.responsedata?.basicInfo;
          const providerTypesList = data?.responsedata?.participantAssociations?.map((provider: any) => ENROLLMENT_TYPE_BACKEND_MAPPINGS[provider.providerType]);
          this.sectionService.setEnrollmentType(JSON.stringify(providerTypesList));
          this.userService.setProviderPplId(data?.responsedata?.infoData?.providerPPLId);
        } else {
          this.savedData = data?.responsedata?.infoData;
          this.userService.setParticipantPplId(data?.responsedata?.infoData?.participantPPLId);
        }

        if (
          this.savedData?.applicationStatus === 'New Application' ||
          this.savedData?.applicationStatus === 'In Progress'
        ) {

          if (entry === STATIC_DATA.PROVIDER_ENROLLMENT) {
            this.storageService.setProviderId(this.savedData?.providerId);
            this.storageService.setTrackingNumber(this.savedData?.trackingNumber);
            this.storageService.setProviderPPLId(this.savedData?.providerPPLId);
            this.referralDataService.setReferralDetails(data?.responsedata);
          }

          if (entry === STATIC_DATA.PARTICIPANT_ENROLLMENT) {
            this.referralDataService.setReferralDetails(data?.responsedata);
            this.storageService.setParticipantId(this.savedData?.participantId);
            this.storageService.setParticipantPPLId(this.savedData?.participantPPLId);
            this.storageService.setTrackingNumber(this.savedData?.trackingNumber);
          }

          setTimeout(() => {
            this.router.navigate(['/newReferral']);
          }, 200);
        } else {
          setTimeout(() => {
            this.router.navigate(['/home/enrollmentDetails']);
          }, 200);
        }
      });
  }
}
